import { Add } from '@mui/icons-material';
import { Alert, Button, IconButton } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CommodityManagementApi, MarkItViewApi } from '../../../../Apis/Apis';
import {
  CommodityModel,
  HedgePlanBudgetTriggerModel,
  HedgePlanModel,
  HedgePlanSeasonModel,
  HedgePlanTermReportModel,
  HedgePlanTimeTriggerModel,
  HedgePlanTradeModel,
  HedgePlanValueTriggerModel,
  ListHedgePlanSeasonsResponse,
  VolumeSource,
} from '../../../../Generated/Commodity-Management-Api';
import {
  CompanyModel,
  MarketViewApplication,
  MarketViewProductModel,
} from '../../../../Generated/Mark-It-View-Api';
import { useLoadingState, useMtmDate } from '../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { StoneXLoading, StoneXRow, StoneXTab, StoneXTabs } from '../../../StoneX';
import StoneXMainPage from '../../../StoneX/StoneXMainPage/StoneXMainPage';
import { StoneXAutocomplete } from '../../../StoneXMui';
import { aggregateTrades, findMarketPriceOnOrBefore } from './HedgePlanChartHelpers';
import HedgePlanCombinedChart, { buildHedgePlanChartOptions, HedgePlanChartEntry, SelectedMatrixBand } from './HedgePlanCombinedChart';
import HedgePlanHedgesTable, { HedgePlanHedgesTableEntry } from './HedgePlanHedgesTable';
import HedgePlanModal from './HedgePlanModal';
import { SeasonPeriod, toSeasonPeriod } from './HedgePlanTerms';
import HedgePlanMatrixTable, { HedgePlanMatrixTableEntry } from './HedgePlanMatrixTable';
import { buildMatrixBands, findMatrixBand, MatrixBand } from './HedgePlanMatrixBands';
import { HedgePlanChartSlide, HedgePlanExportCell } from './exportHedgePlanTable';
import { getDecileTextColor, readDecileColor } from '../DecileMatrixStyles';
import HedgePlanSeasonModal from './HedgePlanSeasonModal';
import HedgePlanVolumeTable from './HedgePlanVolumeTable';
import { DefaultHedgePlanConfiguration, HedgePlanConfiguration } from './Types';

const MAX_PLAN_TABS = 6;
const COMPANY_GUID_STORAGE_KEY = 'hedgePlanCompanyGuid';

interface PeriodReportState {
  report?: HedgePlanTermReportModel;
  loading: boolean;
  error?: string;
  requestId: number;
  contextKey: string;
}

interface CompanyOption {
  companyGuid: string;
  name: string;
}

interface SiteOption {
  siteGuid: string;
  siteName: string;
  companyGuid: string;
}

// All progress for a single hedge plan tab - loaded trigger data, which period is showing,
// pinned matrix cells, and cached reports - lives here, keyed by hedgePlanId, so
// switching between plan tabs never re-fetches or discards what was already loaded.
interface PlanUiState {
  dataLoaded: boolean;
  valueTriggers: HedgePlanValueTriggerModel[];
  budgetTriggers: HedgePlanBudgetTriggerModel[];
  timeTriggers: HedgePlanTimeTriggerModel[];
  trades: HedgePlanTradeModel[];
  seasons: HedgePlanSeasonModel[];
  nextSeason?: HedgePlanSeasonModel;
  // Ended seasons that still have data, which the plan settings can show again.
  previousSeasons: HedgePlanSeasonModel[];
  // Session-only: hidden or ended seasons shown again until the page is left.
  shownSeasonKeys: string[];
  // Session-only override of the plan's saved WeightPriceCurveByVolume setting.
  weightByVolume?: boolean;
  // Session-only: show hedge amounts as volumes instead of percents.
  showVolumes?: boolean;
  selectedPeriodKey?: string;
  selectedMatrixCells: Map<string, SelectedMatrixBand>;
  reportsByPeriodKey: Record<string, PeriodReportState>;
}

const emptyPlanUiState: PlanUiState = {
  dataLoaded: false,
  valueTriggers: [],
  budgetTriggers: [],
  timeTriggers: [],
  trades: [],
  seasons: [],
  nextSeason: undefined,
  previousSeasons: [],
  shownSeasonKeys: [],
  selectedPeriodKey: undefined,
  selectedMatrixCells: new Map(),
  reportsByPeriodKey: {},
};

export default function HedgePlans() {
  const userAccess = useApplicationSelector((state: RootState) => state.userAccess);
  const canOpen = userAccess.isAdmin || userAccess.marketViewApplications.includes(MarketViewApplication.HedgePlan);
  // Everyone else can only view their company's plans.
  const canEdit = userAccess.isAdmin;
  const companiesLoadingState = useLoadingState();
  const sitesLoadingState = useLoadingState();
  const plansLoadingState = useLoadingState();
  const planDataLoadingState = useLoadingState();
  const productsLoadingState = useLoadingState();
  const addSeasonLoadingState = useLoadingState();
  const quoteDate = useMtmDate();

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [commoditiesBySiteGuid, setCommoditiesBySiteGuid] = useState<Record<string, CommodityModel[]>>({});
  const [commoditiesLoading, setCommoditiesLoading] = useState<boolean>(false);

  const [products, setProducts] = useState<MarketViewProductModel[]>([]);

  const [plans, setPlans] = useState<HedgePlanModel[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<HedgePlanModel | undefined>(undefined);

  const [planStatesByPlanId, setPlanStatesByPlanId] = useState<Record<number, PlanUiState>>({});

  const [seasonModalPeriodKey, setSeasonModalPeriodKey] = useState<string | undefined>(undefined);

  const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
  const [editingPlan, setEditingPlan] = useState<HedgePlanModel | undefined>(undefined);
  const [triggerSaveError, setTriggerSaveError] = useState<string | undefined>(undefined);
  const [hoveredPriceByPeriodKey, setHoveredPriceByPeriodKey] = useState<Record<string, number | undefined>>({});
  const [hoveredMatrixCell, setHoveredMatrixCell] = useState<SelectedMatrixBand | undefined>(undefined);

  const nextReportRequestId = useRef(0);

  const configuration = useMemo<HedgePlanConfiguration>(
    () => ({ ...DefaultHedgePlanConfiguration, ...(selectedPlan ? JSON.parse(selectedPlan.configuration) as Partial<HedgePlanConfiguration> : {}) }),
    [selectedPlan],
  );

  const planState =selectedPlan ? planStatesByPlanId[selectedPlan.hedgePlanId] ?? emptyPlanUiState : emptyPlanUiState;
  const {
    valueTriggers,
    budgetTriggers,
    timeTriggers,
    trades,
    seasons,
    nextSeason,
    previousSeasons,
    shownSeasonKeys,
    selectedPeriodKey,
    selectedMatrixCells,
    reportsByPeriodKey,
  } = planState;

  function updatePlanState(hedgePlanId: number, updater: (state: PlanUiState) => PlanUiState) {
    setPlanStatesByPlanId((current) => ({
      ...current,
      [hedgePlanId]: updater(current[hedgePlanId] ?? emptyPlanUiState),
    }));
  }

  useEffect(() => {
    if (!userAccess.isAdmin) {
      // Everyone else sees their own company's plans.
      const companyGuid = userAccess.companyGuid;
      setSelectedCompany(companyGuid ? { companyGuid, name: userAccess.companyName ?? '' } : null);
      return;
    }

    MarkItViewApi.listCompanies(companiesLoadingState.setLoadingState).then((response) => {
      const rows = (response.data.rows ?? []).map((company: CompanyModel) => ({
        companyGuid: company.companyGuid,
        name: company.name,
      }));
      setCompanies(rows);

      // Restore the company from sessionStorage on first load, so refreshing doesn't lose
      // the selection.
      const storedCompanyGuid = sessionStorage.getItem(COMPANY_GUID_STORAGE_KEY);
      if (storedCompanyGuid) {
        const match = rows.find((company) => company.companyGuid === storedCompanyGuid);
        if (match) {
          setSelectedCompany(match);
        }
      }
    });
    MarkItViewApi.listCommodityManagementSites(sitesLoadingState.setLoadingState, false).then((response) => {
      setSites((response.data.rows ?? []).map((site) => ({
        siteGuid: site.siteGuid,
        siteName: site.siteName,
        companyGuid: site.company.companyGuid,
      })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAccess.isAdmin, userAccess.companyGuid]);

  useEffect(() => {
    setPlans([]);
    setSelectedPlan(undefined);
    setProducts([]);
    resetAllPlanData();

    if (!selectedCompany?.companyGuid) {
      return;
    }

    loadPlans(selectedCompany.companyGuid, true);

    MarkItViewApi.listMarketViewProducts(productsLoadingState.setLoadingState).then((response) => {
      setProducts(response.data.rows ?? []);
    });

  }, [selectedCompany?.companyGuid]);

  // The settings window's Commodity Management volume source lists each site's commodities, so they
  // are loaded once when the company is picked rather than every time the window opens.
  const companySites = useMemo(
    () => sites.filter((site) => site.companyGuid === selectedCompany?.companyGuid),
    [sites, selectedCompany?.companyGuid],
  );

  useEffect(() => {
    setCommoditiesBySiteGuid({});

    if (companySites.length === 0) {
      return;
    }

    setCommoditiesLoading(true);
    // A site can differ from the app's active site, so each request pins its site header.
    Promise.all(companySites.map((site) => CommodityManagementApi.getCustomerData(null, {
      headers: { 'X-Site-Guid': site.siteGuid },
    }).then((response) => {
      const rows = [...(response.data.commodities ?? [])]
        .sort((left, right) => left.commodityName.localeCompare(right.commodityName));
      setCommoditiesBySiteGuid((current) => ({ ...current, [site.siteGuid]: rows }));
    }).catch(() => undefined))).finally(() => setCommoditiesLoading(false));
  }, [companySites]);

  // Switching plan tabs only clears transient, cross-plan UI state (an open trigger modal,
  // hover previews) - it never clears a plan's cached triggers/reports/selections, and only
  // fetches trigger data the first time a given plan is visited.
  useEffect(() => {
    setSeasonModalPeriodKey(undefined);
    setTriggerSaveError(undefined);
    setHoveredPriceByPeriodKey({});
    setHoveredMatrixCell(undefined);

    if (!selectedPlan) {
      return;
    }

    if (!planStatesByPlanId[selectedPlan.hedgePlanId]?.dataLoaded) {
      loadPlanData(selectedPlan.hedgePlanId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?.hedgePlanId]);

  function loadPlanData(hedgePlanId: number) {
    return Promise.all([
      CommodityManagementApi.listHedgePlanData(planDataLoadingState.setLoadingState, String(hedgePlanId)),
      CommodityManagementApi.listHedgePlanSeasons(null, String(hedgePlanId)),
    ]).then(([dataResponse, seasonsResponse]) => {
      updatePlanState(hedgePlanId, (state) => ({
        ...state,
        dataLoaded: true,
        valueTriggers: dataResponse.data.valueTriggers ?? [],
        budgetTriggers: dataResponse.data.budgetTriggers ?? [],
        timeTriggers: dataResponse.data.timeTriggers ?? [],
        trades: dataResponse.data.trades ?? [],
        seasons: seasonsResponse.data.rows ?? [],
        nextSeason: seasonsResponse.data.nextSeason,
        previousSeasons: seasonsResponse.data.previousSeasons ?? [],
      }));
    });
  }

  function applySeasons(hedgePlanId: number, response: ListHedgePlanSeasonsResponse) {
    updatePlanState(hedgePlanId, (state) => ({
      ...state,
      seasons: response.rows ?? [],
      nextSeason: response.nextSeason,
      previousSeasons: response.previousSeasons ?? [],
    }));
  }

  function toggleSeasonShown(hedgePlanId: number, periodKey: string) {
    updatePlanState(hedgePlanId, (state) => ({
      ...state,
      shownSeasonKeys: state.shownSeasonKeys.includes(periodKey)
        ? state.shownSeasonKeys.filter((key) => key !== periodKey)
        : [...state.shownSeasonKeys, periodKey],
    }));
  }

  function addNextSeason(hedgePlanId: number) {
    CommodityManagementApi.addHedgePlanSeason(addSeasonLoadingState.setLoadingState, String(hedgePlanId)).then((response) => applySeasons(hedgePlanId, response.data));
  }

  function loadPlans(companyGuid: string, selectFirst: boolean) {
    return CommodityManagementApi.listHedgePlans(plansLoadingState.setLoadingState, companyGuid).then((response) => {
      const rows = response.data.rows ?? [];
      setPlans(rows);
      if (selectFirst && rows.length > 0) {
        setSelectedPlan(rows[0]);
      }
      return rows;
    });
  }

  // Only used when switching companies - a different company's plans are a clean slate, so
  // nothing about the previous company's progress is worth keeping around.
  function resetAllPlanData() {
    setPlanStatesByPlanId({});
    setSeasonModalPeriodKey(undefined);
    setTriggerSaveError(undefined);
    setHoveredPriceByPeriodKey({});
    setHoveredMatrixCell(undefined);
  }

  function onPlanSaved(saved: HedgePlanModel) {
    setPlans((previous) => {
      const exists = previous.some((p) => p.hedgePlanId === saved.hedgePlanId);
      return exists
        ? previous.map((p) => (p.hedgePlanId === saved.hedgePlanId ? saved : p))
        : [...previous, saved];
    });
    // The saved settings (season type, fiscal start month, etc.) can change which periods and
    // reports are valid, so drop this plan's cache and let it reload fresh.
    setPlanStatesByPlanId((current) => {
      const next = { ...current };
      delete next[saved.hedgePlanId];
      return next;
    });
    // Editing keeps the same plan id, so the plan-change effect won't refetch the cleared data.
    if (selectedPlan?.hedgePlanId === saved.hedgePlanId) {
      loadPlanData(saved.hedgePlanId);
    }
    setSelectedPlan(saved);
    setSettingsModalOpen(false);
  }

  // Hidden and ended seasons only show while the user has brought them back for the session.
  const visibleSeasons = useMemo(() => {
    const isShown = (season: HedgePlanSeasonModel) => shownSeasonKeys.includes(toSeasonPeriod(season).key);
    return [...previousSeasons.filter(isShown), ...seasons.filter((season) => !season.isHidden || isShown(season))]
      .sort((left, right) => (left.startDate ?? '').localeCompare(right.startDate ?? ''));
  }, [seasons, previousSeasons, shownSeasonKeys]);
  const allPeriods = useMemo(() => visibleSeasons.map(toSeasonPeriod), [visibleSeasons]);
  const seasonModalSeason = visibleSeasons.find((season) => toSeasonPeriod(season).key === seasonModalPeriodKey);
  const seasonModalPeriod = seasonModalSeason ? toSeasonPeriod(seasonModalSeason) : undefined;
  const hiddenSeasons = useMemo(
    () => [...seasons.filter((season) => season.isHidden), ...previousSeasons],
    [seasons, previousSeasons],
  );

  const hasBudgetSource = !!selectedPlan?.volumeSource && selectedPlan.volumeSource !== VolumeSource.None;
  const canWeightByVolume = hasBudgetSource;
  const weightByVolume = canWeightByVolume && (planState.weightByVolume ?? selectedPlan?.weightPriceCurveByVolume ?? false);
  const showVolumes = canWeightByVolume && (planState.showVolumes ?? false);

  const reportContextKey = selectedPlan
    ? `${selectedPlan.hedgePlanId}|${dayjs(quoteDate).format('YYYY-MM-DD')}|${weightByVolume ? 'weighted' : 'unweighted'}`
    : '';

  useEffect(() => {
    if (!selectedPlan) {
      return;
    }

    if (!selectedPeriodKey || !allPeriods.some((period) => period.key === selectedPeriodKey)) {
      updatePlanState(selectedPlan.hedgePlanId, (state) => ({ ...state, selectedPeriodKey: allPeriods[0]?.key }));
    }

    allPeriods.forEach((period) => {
      if (reportsByPeriodKey[period.key]?.contextKey !== reportContextKey) {
        loadPeriodReport(period, reportContextKey);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPeriods, reportContextKey, selectedPlan?.hedgePlanId]);

  function toggleMatrixCell(entry: HedgePlanMatrixTableEntry, band: MatrixBand) {
    if (!selectedPlan) {
      return;
    }
    const cellKey = `${entry.key}|${band.percentile}`;
    updatePlanState(selectedPlan.hedgePlanId, (state) => {
      const nextCells = new Map(state.selectedMatrixCells);
      if (nextCells.has(cellKey)) {
        nextCells.delete(cellKey);
      } else {
        nextCells.set(cellKey, { periodKey: entry.key, periodLabel: entry.label, band });
      }
      return { ...state, selectedMatrixCells: nextCells };
    });
  }

  function updateSelectedCompany(company: CompanyOption | null) {
    setSelectedCompany(company);
    if (company) {
      sessionStorage.setItem(COMPANY_GUID_STORAGE_KEY, company.companyGuid);
    } else {
      sessionStorage.removeItem(COMPANY_GUID_STORAGE_KEY);
    }
  }

  function openNewPlanModal() {
    setEditingPlan(undefined);
    setSettingsModalOpen(true);
  }

  function openEditPlanModal() {
    setEditingPlan(selectedPlan);
    setSettingsModalOpen(true);
  }

  function loadPeriodReport(period: SeasonPeriod, contextKey: string = reportContextKey) {
    if (!selectedPlan) {
      return;
    }

    const hedgePlanId = selectedPlan.hedgePlanId;
    const requestId = ++nextReportRequestId.current;
    updatePlanState(hedgePlanId, (state) => ({
      ...state,
      reportsByPeriodKey: { ...state.reportsByPeriodKey, [period.key]: { loading: true, requestId, contextKey } },
    }));

    CommodityManagementApi.getHedgePlanReport(
      undefined,
      String(hedgePlanId),
      String(period.year),
      dayjs(quoteDate).format('YYYY-MM-DD'),
      period.term,
      weightByVolume,
    )
      .then((response) => {
        updatePlanState(hedgePlanId, (state) => {
          if (state.reportsByPeriodKey[period.key]?.requestId !== requestId) {
            return state;
          }
          return {
            ...state,
            reportsByPeriodKey: {
              ...state.reportsByPeriodKey,
              [period.key]: {
                report: response.data.report,
                loading: false,
                requestId,
                contextKey,
              },
            },
          };
        });
      })
      .catch((error) => {
        updatePlanState(hedgePlanId, (state) => {
          if (state.reportsByPeriodKey[period.key]?.requestId !== requestId) {
            return state;
          }
          return {
            ...state,
            reportsByPeriodKey: {
              ...state.reportsByPeriodKey,
              [period.key]: {
                loading: false,
                error: error?.response?.data?.detail ?? error?.response?.data?.message ?? 'The hedge plan could not be run.',
                requestId,
                contextKey,
              },
            },
          };
        });
      });
  }

  const usePlanDropdown = plans.length > MAX_PLAN_TABS;
  const activePlanTabIndex = useMemo(
    () => (selectedPlan ? plans.findIndex((p) => p.hedgePlanId === selectedPlan.hedgePlanId) : -1),
    [plans, selectedPlan],
  );

  const loadedChartEntries = useMemo(
    () => allPeriods.reduce<HedgePlanChartEntry[]>((entries, period) => {
      const reportState = reportsByPeriodKey[period.key];
      const matrix = reportState?.report;
      if (!matrix || reportState.contextKey !== reportContextKey) {
        return entries;
      }

      entries.push({
        periodKey: period.key,
        periodLabel: period.label,
        seasonEndDate: period.endDate,
        matrix,
        valueTriggers: valueTriggers.filter((trigger) => trigger.year === period.year && trigger.term === period.term),
        budgetTriggers: budgetTriggers.filter((trigger) => trigger.year === period.year && trigger.term === period.term),
        timeTriggers: timeTriggers.filter((trigger) => trigger.year === period.year && trigger.term === period.term),
        // The report's trades carry both percent and volume.
        trades: matrix.summary?.trades ?? trades.filter((trade) => trade.year === period.year && trade.term === period.term),
      });
      return entries;
    }, []),
    [allPeriods, reportsByPeriodKey, reportContextKey, valueTriggers, budgetTriggers, timeTriggers, trades],
  );

  const selectedChartEntries = useMemo(
    () => loadedChartEntries.filter((entry) => entry.periodKey === selectedPeriodKey),
    [loadedChartEntries, selectedPeriodKey],
  );

  // Unlike `loadedChartEntries` (which only includes a period once its matrix report finishes),
  // this covers every period as soon as the plan's trigger/trade data is in - the report is
  // attached when/if it's ready, but hedge position and recommendation don't need to wait for it.
  const hedgesTableEntries = useMemo<HedgePlanHedgesTableEntry[]>(() => {
    if (!planState.dataLoaded) {
      return [];
    }
    return allPeriods.map((period) => {
      const reportState = reportsByPeriodKey[period.key];
      const matrix = reportState?.contextKey === reportContextKey ? reportState.report : undefined;
      return {
        periodKey: period.key,
        periodLabel: period.label,
        matrix,
        valueTriggers: valueTriggers.filter((trigger) => trigger.year === period.year && trigger.term === period.term),
        budgetTriggers: budgetTriggers.filter((trigger) => trigger.year === period.year && trigger.term === period.term),
        timeTriggers: timeTriggers.filter((trigger) => trigger.year === period.year && trigger.term === period.term),
        trades: trades.filter((trade) => trade.year === period.year && trade.term === period.term),
      };
    });
  }, [planState.dataLoaded, allPeriods, reportsByPeriodKey, reportContextKey, valueTriggers, budgetTriggers, timeTriggers, trades]);

  // Shared by the chart-hover and table-hover handlers below, so both resolve a price to a
  // band using the exact same lookup instead of two independent implementations drifting apart.
  const matrixBandsByPeriodKey = useMemo(
    () => new Map(selectedChartEntries.map((entry) => [entry.periodKey, buildMatrixBands(entry.matrix)])),
    [selectedChartEntries],
  );

  // Mirrors the matrix table's own default: a pinned band always wins; otherwise an actively
  // hovered entry shows its hovered band; otherwise (matching the table's un-hovered default)
  // the entry shows the band holding its last close, so the chart and table agree on load
  // instead of the chart staying blank until the user hovers something.
  const chartedMatrixCells = useMemo(() => {
    const pinnedKeys = new Set(selectedMatrixCells.keys());
    const result = Array.from(selectedMatrixCells.values());

    selectedChartEntries.forEach((entry) => {
      if (hoveredMatrixCell?.periodKey === entry.periodKey) {
        const hoveredKey = `${hoveredMatrixCell.periodKey}|${hoveredMatrixCell.band.percentile}`;
        if (!pinnedKeys.has(hoveredKey)) {
          result.push(hoveredMatrixCell);
        }
        return;
      }

      const hasPinnedForEntry = Array.from(pinnedKeys).some((key) => key.startsWith(`${entry.periodKey}|`));
      if (hasPinnedForEntry) {
        return;
      }

      const pricedPoints = entry.matrix.priceSeries.filter((point) => point.price !== null && point.price !== undefined);
      const lastClosePrice = pricedPoints[pricedPoints.length - 1]?.price as number | undefined;
      const band = findMatrixBand(matrixBandsByPeriodKey.get(entry.periodKey) ?? [], lastClosePrice);
      if (band) {
        result.push({ periodKey: entry.periodKey, periodLabel: entry.periodLabel, band });
      }
    });

    return result;
  }, [selectedMatrixCells, hoveredMatrixCell, selectedChartEntries, matrixBandsByPeriodKey]);

  // Hovering the price line on the chart mirrors hovering a decile cell in the table: it
  // highlights that entry's row in the table (via hoveredPriceByPeriodKey) and previews the
  // matching band on the chart (via hoveredMatrixCell) - the same "previewed band" state that
  // hovering a table cell sets directly further down.
  function updateHoveredPrice(periodKey: string, price: number | undefined) {
    setHoveredPriceByPeriodKey((current) => ({ ...current, [periodKey]: price }));

    if (price === undefined) {
      setHoveredMatrixCell((current) => (current?.periodKey === periodKey ? undefined : current));
      return;
    }

    const band = findMatrixBand(matrixBandsByPeriodKey.get(periodKey) ?? [], price);
    if (!band) {
      return;
    }

    const periodLabel = selectedChartEntries.find((entry) => entry.periodKey === periodKey)?.periodLabel ?? periodKey;
    setHoveredMatrixCell({ periodKey, periodLabel, band });
  }

  const loadingPeriodKeys = useMemo(
    () => new Set(allPeriods
      .filter((period) => {
        const reportState = reportsByPeriodKey[period.key];
        return reportState?.contextKey !== reportContextKey || reportState.loading;
      })
      .map((period) => period.key)),
    [allPeriods, reportsByPeriodKey, reportContextKey],
  );

  // A chart and matrix slide for each season whose report has run, for the PowerPoint. Each chart shows the band
  // holding the latest price, as the page does before a band is picked. The matrix follows HedgePlanMatrixTable.
  function getChartSlides(): HedgePlanChartSlide[] {
    if (!selectedPlan) {
      return [];
    }

    return loadedChartEntries.map((entry) => {
      const bands = buildMatrixBands(entry.matrix);
      const pricedPoints = entry.matrix.priceSeries.filter((point) => point.price !== null && point.price !== undefined);
      const latestBand = findMatrixBand(bands, pricedPoints[pricedPoints.length - 1]?.price as number | undefined);

      const matrixRows: HedgePlanExportCell[][] = [
        [{ text: 'Decile' }, { text: entry.periodLabel }],
        ...bands.map((band) => {
          const isLatest = band.percentile === latestBand?.percentile;
          return [
            { text: band.label, fill: readDecileColor(band.percentile), color: getDecileTextColor(band.percentile), bold: true },
            {
              text: `${band.lowValue.toFixed(3)} - ${band.highValue.toFixed(3)}`,
              fill: isLatest ? readDecileColor(band.percentile) : undefined,
              color: isLatest ? getDecileTextColor(band.percentile) : undefined,
              bold: isLatest,
            },
          ];
        }),
      ];

      return {
        periodKey: entry.periodKey,
        title: `${selectedPlan.name} Hedge Plan: ${entry.periodLabel}`,
        chartOptions: buildHedgePlanChartOptions({
          entries: [entry],
          matrixCellSelections: latestBand ? [{ periodKey: entry.periodKey, periodLabel: entry.periodLabel, band: latestBand }] : [],
          showTimeTriggers: configuration.chartSettings.showTimeTriggers,
          snapTradesToMarketPrice: configuration.chartSettings.snapTradesToMarketPrice,
          showVolumes,
        }),
        matrixRows,
      };
    });
  }

  if (!canOpen) {
    return <Navigate to="/" replace />;
  }

  return (
    <StoneXMainPage>
      <StoneXRow>
        <h3>Hedge Plans</h3>
      </StoneXRow>

      {userAccess.isAdmin && (
        <StoneXRow>
          <StoneXAutocomplete
            label="Company"
            options={companies}
            value={selectedCompany}
            getOptionLabel={(company: CompanyOption) => company.name}
            isOptionEqualToValue={(a: CompanyOption, b: CompanyOption) => a.companyGuid === b.companyGuid}
            onUpdate={updateSelectedCompany}
            placeholder="Select a company"
            loading={companiesLoadingState.isLoading}
            width="360px"
          />
        </StoneXRow>
      )}

      {selectedCompany && !usePlanDropdown && (
        <StoneXTabs activeTabIndex={activePlanTabIndex}>
          {[
            ...plans.map((plan, index) => (
              <StoneXTab
                key={plan.hedgePlanId}
                tabIndex={index}
                isActive={activePlanTabIndex === index}
                onClick={() => setSelectedPlan(plan)}
                label={plan.name}
              />
            )),
            ...(canEdit ? [
              <StoneXTab
                key={-1}
                tabIndex={plans.length}
                onClick={openNewPlanModal}
                label={<IconButton><Add fontSize="medium" /></IconButton>}
              />,
            ] : []),
          ]}
        </StoneXTabs>
      )}

      {selectedCompany && usePlanDropdown && (
        <StoneXRow>
          <StoneXAutocomplete
            label="Hedge Plan"
            options={plans}
            value={selectedPlan ?? null}
            getOptionLabel={(plan: HedgePlanModel) => plan.name}
            isOptionEqualToValue={(a: HedgePlanModel, b: HedgePlanModel) => a.hedgePlanId === b.hedgePlanId}
            onUpdate={(plan: HedgePlanModel | null) => plan && setSelectedPlan(plan)}
            placeholder="Select a hedge plan"
            width="360px"
          />
          {canEdit && (
            <IconButton onClick={openNewPlanModal} aria-label="New hedge plan">
              <Add />
            </IconButton>
          )}
        </StoneXRow>
      )}

      <StoneXRow marginDirection='top'>
        <StoneXLoading show={plansLoadingState.isLoading() || planDataLoadingState.isLoading()} />    
      </StoneXRow>

      {selectedPlan && (
        <div style={{ margin: '20px 0' }}>
            <HedgePlanHedgesTable
              entries={hedgesTableEntries}
              periods={allPeriods.map((period) => ({ key: period.key, label: period.label }))}
              dataLoading={!planState.dataLoaded}
              loadingPeriodKeys={loadingPeriodKeys}
              selectedPeriodKey={selectedPeriodKey}
              onToggleChart={(periodKey) => updatePlanState(selectedPlan.hedgePlanId, (state) => ({
                ...state,
                selectedPeriodKey: state.selectedPeriodKey === periodKey ? undefined : periodKey,
              }))}
              onManageSeason={setSeasonModalPeriodKey}
              showBudgetPrice={hasBudgetSource}
              nextSeasonLabel={nextSeason?.label ?? ''}
              onAddNextSeason={() => addNextSeason(selectedPlan.hedgePlanId)}
              addingNextSeason={addSeasonLoadingState.isLoading()}
              onOpenSettings={openEditPlanModal}
              exportTitle={selectedPlan.name}
              getChartSlides={getChartSlides}
              weightByVolume={weightByVolume}
              canWeightByVolume={canWeightByVolume}
              onToggleWeightByVolume={() => updatePlanState(selectedPlan.hedgePlanId, (state) => ({
                ...state,
                weightByVolume: !weightByVolume,
              }))}
              showVolumes={showVolumes}
              onToggleShowVolumes={() => updatePlanState(selectedPlan.hedgePlanId, (state) => ({
                ...state,
                showVolumes: !state.showVolumes,
              }))}
              readOnly={!canEdit}
            />
        </div>
      )}

      {triggerSaveError && (
        <Alert severity="error" onClose={() => setTriggerSaveError(undefined)} sx={{ marginTop: 1 }}>
          {triggerSaveError} Your previous trigger values have been restored.
        </Alert>
      )}

      {allPeriods.map((period) => {
        const periodReportState = reportsByPeriodKey[period.key];
        return periodReportState?.error && periodReportState.contextKey === reportContextKey ? (
          <StoneXRow key={period.key}>
            <Alert
              severity="error"
              action={<Button color="inherit" onClick={() => loadPeriodReport(period)}>Retry</Button>}
            >
              {period.label}: {periodReportState.error}
            </Alert>
        </StoneXRow>
        ) : null;
      })}

      {selectedChartEntries.length > 0 && (
        <div style={{ display: 'flex', flex: 1, gap: 16, marginTop: 20, minWidth: 0, overflowX: 'auto' }}>
          <div style={{ flex: 1, minWidth: 0, width: 0 }}>
            <HedgePlanCombinedChart
              entries={selectedChartEntries}

              matrixCellSelections={chartedMatrixCells}
              onHoveredPriceChange={updateHoveredPrice}
              showTimeTriggers={configuration.chartSettings.showTimeTriggers}
              snapTradesToMarketPrice={configuration.chartSettings.snapTradesToMarketPrice}
              showVolumes={showVolumes}
            />
          </div>

          <HedgePlanMatrixTable
            entries={selectedChartEntries.map((entry) => {
              const pricedPoints = entry.matrix.priceSeries.filter((point) => point.price !== null && point.price !== undefined);
              return {
                key: entry.periodKey,
                label: entry.periodLabel,
                matrix: entry.matrix,
                lastClosePrice: pricedPoints.length > 0 ? pricedPoints[pricedPoints.length - 1].price as number : undefined,
                hoveredPrice: hoveredPriceByPeriodKey[entry.periodKey],
              };
            })}
            selectedCellKeys={new Set(selectedMatrixCells.keys())}
            onCellClick={toggleMatrixCell}
            onCellHover={(entry, band) => setHoveredMatrixCell({ periodKey: entry.key, periodLabel: entry.label, band })}
            onCellHoverEnd={() => setHoveredMatrixCell(undefined)}
          />
        </div>
      )}

      {selectedChartEntries.map((entry) => (
        <HedgePlanVolumeTable key={entry.periodKey} periodLabel={entry.periodLabel} report={entry.matrix} />
      ))}



      {selectedChartEntries.map((entry) => {
        const periodExecutions = aggregateTrades(entry.trades);
        const unplottedExecutionDates = periodExecutions
          .filter((execution) => findMarketPriceOnOrBefore(entry.matrix, execution.executionDate) === undefined)
          .map((execution) => execution.executionDate.substring(0, 10));

        return unplottedExecutionDates.length > 0 ? (
          <div key={entry.periodKey} style={{ marginTop: 8 }}>
            {unplottedExecutionDates.length > 0 && (
              <Alert severity="warning">
                {`No market price was available on or before ${unplottedExecutionDates.join(', ')}, so ${unplottedExecutionDates.length === 1 ? 'that execution is' : 'those executions are'} not shown on the chart.`}
              </Alert>
            )}
          </div>
        ) : null;
      })}

      {selectedCompany && canEdit && (
        <HedgePlanModal
          open={settingsModalOpen}
          onClose={() => setSettingsModalOpen(false)}
          onSaved={onPlanSaved}
          companyGuid={selectedCompany.companyGuid}
          existing={editingPlan}
          products={products}
          sites={companySites}
          commoditiesBySiteGuid={commoditiesBySiteGuid}
          commoditiesLoading={commoditiesLoading}
          hiddenSeasons={editingPlan && editingPlan.hedgePlanId === selectedPlan?.hedgePlanId ? hiddenSeasons : []}
          shownSeasonKeys={shownSeasonKeys}
          onToggleSeasonShown={(periodKey) => selectedPlan && toggleSeasonShown(selectedPlan.hedgePlanId, periodKey)}
        />
      )}

      {selectedPlan && seasonModalSeason && seasonModalPeriod && canEdit && (
        <HedgePlanSeasonModal
          open={!!seasonModalPeriodKey}
          onClose={() => setSeasonModalPeriodKey(undefined)}
          hedgePlanId={selectedPlan.hedgePlanId}
          planType={selectedPlan.planType}
          hasBudgetSource={hasBudgetSource}
          season={seasonModalSeason}
          period={seasonModalPeriod}
          isLastSeason={seasons[seasons.length - 1] !== undefined && toSeasonPeriod(seasons[seasons.length - 1]).key === seasonModalPeriod.key}
          report={reportsByPeriodKey[seasonModalPeriod.key]?.report}
          valueTriggers={valueTriggers}
          budgetTriggers={budgetTriggers}
          timeTriggers={timeTriggers}
          trades={trades}
          enableValueTriggers={configuration.enableValueTriggers}
          enableBudgetTriggers={configuration.enableBudgetTriggers}
          enableTimeTriggers={configuration.enableTimeTriggers}
          onSaveFailed={(message) => {
            setTriggerSaveError(message);
            loadPlanData(selectedPlan.hedgePlanId);
          }}
          onTriggersSaved={(period, savedValueTriggers, savedBudgetTriggers, savedTimeTriggers) => {
            const belongsToPeriod = (trigger: { year: number; term: string }) => trigger.year === period.year && trigger.term === period.term;
            setTriggerSaveError(undefined);
            updatePlanState(selectedPlan.hedgePlanId, (state) => ({
              ...state,
              valueTriggers: [...state.valueTriggers.filter((trigger) => !belongsToPeriod(trigger)), ...savedValueTriggers],
              budgetTriggers: [...state.budgetTriggers.filter((trigger) => !belongsToPeriod(trigger)), ...savedBudgetTriggers],
              timeTriggers: [...state.timeTriggers.filter((trigger) => !belongsToPeriod(trigger)), ...savedTimeTriggers],
            }));
            // The season's summary (recommendation, fulfillment) comes from its report.
            loadPeriodReport(period);
          }}
          onTradesSaved={(period, savedTrades) => {
            const belongsToPeriod = (trade: { year: number; term: string }) => trade.year === period.year && trade.term === period.term;
            setTriggerSaveError(undefined);
            updatePlanState(selectedPlan.hedgePlanId, (state) => ({
              ...state,
              trades: [...state.trades.filter((trade) => !belongsToPeriod(trade)), ...savedTrades],
            }));
            loadPeriodReport(period);
          }}
          onSeasonsChanged={(response) => {
            applySeasons(selectedPlan.hedgePlanId, response);
            // A deleted season's triggers are gone, and a budget adjustment changes the season's report.
            loadPlanData(selectedPlan.hedgePlanId);
            loadPeriodReport(seasonModalPeriod);
          }}
        />
      )}
    </StoneXMainPage>
  );
}
